/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EvaluationContextProvider } from './context/EvaluationContextProvider';
import { EvaluationLineDecoration } from './EvaluationLineDecoration';
import { AssistElement, Evaluation, MathInterpretation, PersistentEvaluation } from '../../types/model';
import { Styled as S } from '../VerticalSolver.styled';
import {
    applyAssistFiltration,
    calculateParts,
    convertToStoreModel,
    fecthMathAssist,
    getPartIndexForSkip,
    retrieveSemanticAssist,
} from './utils/evaluationUtils';
import { SolverContext } from './context/SolverContextProvider';
import { PopoverContextProvider } from './context/PopoverContextProvider';
import { usePreviousState } from '../../hooks/usePreviousState';
import { MathAssistVisualizer } from './MathAssistVisualizer';
import { debounce } from '../constants';

interface EvaluationEditorProps {
    solutionId: number;
    persistentEvaluation: PersistentEvaluation;

    onUpdate(evaluation: PersistentEvaluation): void;

    onDelete(evaluationId: number): void;
}

export const EvaluationEditor: FC<EvaluationEditorProps> = ({ solutionId, persistentEvaluation, onUpdate, onDelete }) => {
    const [evaluation, setEvaluation] = useState<Evaluation>(() => {
        const lines = persistentEvaluation.lines;
        const line = lines[lines.length - 1];
        const parts = calculateParts(line.tree);
        return {
            id: persistentEvaluation.id,
            focused: true,
            dirty: false,
            nodeIdsNearCursor: undefined,
            activeLine: {
                lineId: line.id,
                activePartIndex: 0,
                partsCount: parts.length,
            },
            lines: persistentEvaluation.lines.map((l) => ({
                id: l.id,
                tree: l.tree,
                firstEqualityX: 0,
                lastEqualityX: 0,
            })),
        };
    });

    const { mathMessages, problem, config } = useContext(SolverContext);
    const [semanticAssist, setSemanticAssist] = useState<AssistElement[]>(() => []);
    const [mathAssist, setMathAssist] = useState<AssistElement[]>(() => []);
    const [mathInterpretation, setMathInterpretation] = useState<MathInterpretation>();
    const [previousCursor, setPreviousCursor] = usePreviousState<{
        lineId: string;
        activePartIndex: number;
        partsCount: number;
    }>(evaluation.activeLine);
    const ref = useRef<Promise<AssistElement[]>>();

    useEffect(() => {
        if (evaluation.dirty) {
            const { lineId, activePartIndex } = evaluation.activeLine;
            setMathAssist((prevState) => applyAssistFiltration(prevState, lineId, activePartIndex));
            setSemanticAssist((prevState) => prevState.filter((a) => !(a.partIndex === activePartIndex && a.lineId === lineId)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [evaluation.dirty]);

    useEffect(() => {
        setPreviousCursor(evaluation.activeLine);
    }, [setPreviousCursor, evaluation.activeLine]);

    const fetchAssist = useCallback(
        (evaluation: Evaluation) => fecthMathAssist(evaluation, solutionId, problem, setMathInterpretation),
        [solutionId, problem, setMathInterpretation]
    );

    const handlePartUpdate = useCallback(
        (evaluation: Evaluation) => {
            if (evaluation.lines.length === 0) {
                onDelete(evaluation.id);
                return;
            }
            onUpdate(convertToStoreModel(evaluation));
            setSemanticAssist(retrieveSemanticAssist(evaluation, getPartIndexForSkip(previousCursor, evaluation.activeLine)));
            if (!config.immediateAssist) {
                fetchAssist(evaluation).then(setMathAssist);
            }
        },
        [onDelete, onUpdate, previousCursor, config.immediateAssist, fetchAssist]
    );

    const onStateUpdate = useCallback(
        debounce((evaluation: Evaluation) => {
            const promise = fetchAssist(evaluation);
            ref.current = promise;
            promise.then((response) => ref.current === promise && setMathAssist(response));
        }, config.assistDelay),
        [config, fetchAssist, setMathAssist, ref]
    );

    const handleStateUpdate = useCallback(
        (evaluation: Evaluation) => {
            if (config.immediateAssist) {
                const { lineId, activePartIndex } = evaluation.activeLine;
                setMathAssist((prevState) => applyAssistFiltration(prevState, lineId, activePartIndex));
                onStateUpdate(evaluation);
            }
        },
        [config.immediateAssist, onStateUpdate]
    );

    const handleDelete = useCallback(() => onDelete(persistentEvaluation.id), [onDelete, persistentEvaluation.id]);

    const assistByLine = useMemo(
        () =>
            semanticAssist
                .concat(mathAssist)
                .filter((a) => a.visible)
                .map((e) => (e.messageCode && mathMessages.size ? { ...e, message: mathMessages.get(e.messageCode) } : e))
                .reduce((p, c) => {
                    const prev = p.get(c.lineId);
                    p.set(c.lineId, prev ? prev.concat(c) : [c]);
                    return p;
                }, new Map<string, AssistElement[]>()),
        [semanticAssist, mathAssist, mathMessages]
    );

    return (
        <MathAssistVisualizer mathInterpretation={mathInterpretation}>
            <S.EvaluationComponent>
                <S.Evaluation>
                    <EvaluationContextProvider
                        evaluation={evaluation}
                        dispatchEvaluation={setEvaluation}
                        onPartUpdate={handlePartUpdate}
                        onStateUpdate={handleStateUpdate}
                    >
                        <PopoverContextProvider available={!evaluation.dirty || config.immediateAssist}>
                            {evaluation.lines.map((l) => (
                                <EvaluationLineDecoration
                                    key={l.id}
                                    line={l}
                                    assist={assistByLine.get(l.id)}
                                    currentPartIndex={
                                        l.id === evaluation.activeLine.lineId && evaluation.focused ? evaluation.activeLine.activePartIndex : undefined
                                    }
                                    nodeIdsNearCursor={evaluation.nodeIdsNearCursor}
                                />
                            ))}
                        </PopoverContextProvider>
                    </EvaluationContextProvider>
                </S.Evaluation>
                <S.RemoveBtn onClick={handleDelete}>+</S.RemoveBtn>
            </S.EvaluationComponent>
        </MathAssistVisualizer>
    );
};
